import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import teacherService from '../services/teacherService';
import { toast } from 'react-toastify';
import { doc, setDoc } from 'firebase/firestore'; // Import setDoc directly if needed or use service
import { db } from '../services/firebase';
import './BecomeTeacher.css';

const BecomeTeacher = () => {
  const { currentUser, userProfile, loadUserProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    subjects: [],
    hourlyRate: '',
    bio: '',
    phone: ''
  });

  const availableSubjects = [
    'English', 'Mathematics', 'Science', 'Programming', 'Music', 
    'Art', 'Business', 'Spanish', 'French', 'German'
  ];

  const handleSubjectChange = (subject) => {
    setFormData(prev => {
      const exists = prev.subjects.includes(subject);
      if (exists) {
        return { ...prev, subjects: prev.subjects.filter(s => s !== subject) };
      } else {
        return { ...prev, subjects: [...prev.subjects, subject] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.subjects.length === 0) {
      toast.warning('Please select at least one subject to teach.');
      return;
    }

    setLoading(true);

    try {
      // Servis üzerinden yükseltme işlemini yap
      await teacherService.upgradeToTeacher(currentUser.uid, {
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        email: userProfile.email,
        photoURL: userProfile.photoURL,
        ...formData
      });

      // Profil bilgilerini yeniden yükle (Context'i güncellemek için)
      await loadUserProfile(currentUser.uid);

      toast.success('🎉 Congratulations! You are now a teacher.');
      navigate('/profile'); // Profil sayfasına yönlendir
    } catch (error) {
      console.error('Application error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 1. Durum: Zaten Öğretmen
  if (userProfile?.role === 'teacher') {
    return (
      <div className="become-teacher-page container text-center" style={{padding: '100px 20px'}}>
        <div style={{fontSize: '64px', marginBottom: '20px'}}>👨‍🏫</div>
        <h1>You are already a teacher!</h1>
        <p className="lead">Manage your profile, lessons and availability from your dashboard.</p>
        <button onClick={() => navigate('/profile')} className="btn btn-primary btn-lg mt-4">
          Go to My Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="become-teacher-page">
      {/* Hero Section */}
      <section className="teacher-hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Share Your Knowledge, <br/>Inspire Students Worldwide</h1>
            <p>Join our community of expert tutors and start earning on your own schedule.</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section container">
        <div className="benefit-card">
          <div className="benefit-icon">💰</div>
          <h3>Earn Money</h3>
          <p>Set your own hourly rates and keep up to 90% of your earnings.</p>
        </div>
        <div className="benefit-card">
          <div className="benefit-icon">📅</div>
          <h3>Flexible Schedule</h3>
          <p>Be your own boss. Teach as much or as little as you want.</p>
        </div>
        <div className="benefit-card">
          <div className="benefit-icon">🌍</div>
          <h3>Teach Anywhere</h3>
          <p>Connect with students globally from the comfort of your home.</p>
        </div>
      </section>

      {/* Application Form or Login Prompt */}
      <section className="application-section container">
        {!currentUser ? (
          // 2. Durum: Giriş Yapmamış
          <div className="login-prompt-card">
            <h2>Ready to get started?</h2>
            <p>Create an account or login to apply as a teacher.</p>
            <div className="auth-buttons-large">
              <button onClick={() => navigate('/signup')} className="btn btn-primary btn-lg">
                Create Account
              </button>
              <button onClick={() => navigate('/login')} className="btn btn-outline btn-lg">
                Login
              </button>
            </div>
          </div>
        ) : (
          // 3. Durum: Öğrenci (Başvuru Formu)
          <div className="application-form-card">
            <h2>Teacher Application</h2>
            <p className="form-subtitle">Complete your profile to start teaching</p>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Tell us about yourself (Bio)</label>
                <textarea
                  className="form-textarea"
                  rows="4"
                  placeholder="I am an experienced math teacher..."
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Hourly Rate (£)</label>
                <input
                  type="number"
                  className="form-input"
                  min="5"
                  max="200"
                  placeholder="e.g. 20"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({...formData, hourlyRate: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Subjects you can teach</label>
                <div className="subjects-grid-selector">
                  {availableSubjects.map(subject => (
                    <div 
                      key={subject}
                      className={`subject-item ${formData.subjects.includes(subject) ? 'selected' : ''}`}
                      onClick={() => handleSubjectChange(subject)}
                    >
                      {subject}
                    </div>
                  ))}
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-lg btn-block"
                disabled={loading}
              >
                {loading ? <div className="spinner"></div> : '🚀 Submit Application'}
              </button>
            </form>
          </div>
        )}
      </section>
    </div>
  );
};

export default BecomeTeacher;